import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Table, Tag, Upload, message } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { downloadFile, errorMessage } from '../../api/client';
import {
  fetchDealerPlacementImageUrl,
  fetchDealerReceiptImageUrl,
  fetchDealerReturnImageUrl,
  useDealerPlacements,
  useDealerReceipts,
  useDealerReturns,
  useUploadDealerPlacementImage,
  useUploadDealerReceiptImage,
  useUploadDealerReturnImage,
} from '../../api/hooks';
import { date, money } from '../../utils/format';
import TransactionImageLink from '../investor/TransactionImageLink';
import { useTranslation } from 'react-i18next';

interface ReceiptRow {
  key: string;
  date: string;
  type: 'Investment' | 'Withdrawal' | 'Profit Payment';
  /** Row id on its own table — the three types have independent id sequences. */
  refId: number;
  reference: string;
  amount: number;
  url: string;
  fileName: string;
  hasTransactionImage: boolean;
}

const TYPE_COLOR: Record<ReceiptRow['type'], string> = {
  Investment: 'blue',
  Withdrawal: 'orange',
  'Profit Payment': 'green',
};

/** Every transaction PDF for this dealer in one place — mirrors the investor Receipts tab exactly. */
export default function DealerReceiptsTab({ dealerId }: { dealerId: number }) {
  const { t } = useTranslation();
  const { data: placements, isLoading: loadingPlacements } = useDealerPlacements(dealerId);
  const { data: returns, isLoading: loadingReturns } = useDealerReturns(dealerId);
  const { data: receipts, isLoading: loadingReceipts } = useDealerReceipts(dealerId);

  const uploadPlacement = useUploadDealerPlacementImage(dealerId);
  const uploadReturn = useUploadDealerReturnImage(dealerId);
  const uploadReceipt = useUploadDealerReceiptImage(dealerId);

  const rows: ReceiptRow[] = [
    ...(placements ?? []).map((p) => ({
      key: `inv-${p.id}`,
      date: p.placementDate,
      type: 'Investment' as const,
      refId: p.id,
      reference: `INV-R-${String(p.id).padStart(6, '0')}`,
      amount: p.amount,
      url: `/receipts/dealer-investment/${p.id}`,
      fileName: `investment_receipt_${p.id}.pdf`,
      hasTransactionImage: p.hasTransactionImage,
    })),
    ...(returns ?? []).map((r) => ({
      key: `wd-${r.id}`,
      date: r.returnDate,
      type: 'Withdrawal' as const,
      refId: r.id,
      reference: `WD-R-${String(r.id).padStart(6, '0')}`,
      amount: r.amount,
      url: `/receipts/dealer-withdrawal/${r.id}`,
      fileName: `withdrawal_receipt_${r.id}.pdf`,
      hasTransactionImage: r.hasTransactionImage,
    })),
    ...(receipts ?? []).map((r) => ({
      key: `pp-${r.id}`,
      date: r.receiptDate,
      type: 'Profit Payment' as const,
      refId: r.id,
      reference: `PP-R-${String(r.id).padStart(6, '0')}`,
      amount: r.amount,
      url: `/receipts/dealer-profit-payment/${r.id}`,
      fileName: `profit_payment_receipt_${r.id}.pdf`,
      hasTransactionImage: r.hasTransactionImage,
    })),
  ].sort((a, b) => b.date.localeCompare(a.date));

  /** LIST_IGNORE keeps antd from rendering its own file list under the button. */
  const upload = (row: ReceiptRow) => async (file: RcFile) => {
    try {
      if (row.type === 'Investment') await uploadPlacement.mutateAsync({ placementId: row.refId, file });
      else if (row.type === 'Withdrawal') await uploadReturn.mutateAsync({ returnId: row.refId, file });
      else await uploadReceipt.mutateAsync({ receiptId: row.refId, file });
      message.success('Transaction proof uploaded.');
    } catch (err) {
      message.error(errorMessage(err));
    }
    return Upload.LIST_IGNORE;
  };

  const fetchImage = (row: ReceiptRow) => {
    if (row.type === 'Investment') return fetchDealerPlacementImageUrl(dealerId, row.refId);
    if (row.type === 'Withdrawal') return fetchDealerReturnImageUrl(dealerId, row.refId);
    return fetchDealerReceiptImageUrl(dealerId, row.refId);
  };

  return (
    <Table<ReceiptRow>
      rowKey="key"
      loading={loadingPlacements || loadingReturns || loadingReceipts}
      dataSource={rows}
      scroll={{ x: 'max-content' }}
      columns={[
        { title: t('common.date'), dataIndex: 'date', render: date },
        {
          title: t('common.type'),
          dataIndex: 'type',
          render: (value: ReceiptRow['type']) => <Tag color={TYPE_COLOR[value]}>{value}</Tag>,
        },
        { title: t('finance.receiptNo'), dataIndex: 'reference' },
        { title: t('common.amount'), dataIndex: 'amount', align: 'right', render: money },
        {
          title: 'Download',
          render: (_, row) => (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadFile(row.url, row.fileName).catch((err) => message.error(errorMessage(err)))}
            >
              PDF
            </Button>
          ),
        },
        {
          title: t('finance.transactionProof'),
          render: (_, row) =>
            row.hasTransactionImage ? (
              <TransactionImageLink
                exists
                title={`Transaction proof — ${row.reference}`}
                fetchUrl={() => fetchImage(row)}
              />
            ) : (
              <Upload accept=".jpg,.jpeg,.png,.webp" showUploadList={false} beforeUpload={upload(row)}>
                <Button type="link" size="small" icon={<UploadOutlined />}>
                  Upload
                </Button>
              </Upload>
            ),
        },
      ]}
    />
  );
}
